import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { SelectItem } from "@/components/ui/select";

interface ServiceSelectItemProps extends React.ComponentPropsWithoutRef<typeof SelectItem> {
  description?: string;
}

/**
 * SelectItem variant that renders a branded tooltip in a portal on hover/highlight,
 * so the tooltip can escape the SelectContent's overflow boundary.
 */
export const ServiceSelectItem = React.forwardRef<
  React.ElementRef<typeof SelectItem>,
  ServiceSelectItemProps
>(({ description, children, ...props }, ref) => {
  const [open, setOpen] = React.useState(false);
  const itemRef = React.useRef<HTMLDivElement | null>(null);

  // Sync tooltip open state with the SelectItem's data-highlighted attribute
  // (Radix sets this on keyboard nav and mouse hover within the Select).
  React.useEffect(() => {
    const el = itemRef.current;
    if (!el || !description) return;
    const sync = () => setOpen(el.getAttribute("data-highlighted") !== null);
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(el, { attributes: true, attributeFilter: ["data-highlighted"] });
    return () => observer.disconnect();
  }, [description]);

  if (!description) {
    return (
      <SelectItem ref={ref} {...props}>
        {children}
      </SelectItem>
    );
  }

  return (
    <TooltipPrimitive.Root open={open} delayDuration={0}>
      <TooltipPrimitive.Trigger asChild>
        <SelectItem
          ref={(node) => {
            itemRef.current = node as HTMLDivElement | null;
            if (typeof ref === "function") ref(node);
            else if (ref) (ref as React.MutableRefObject<typeof node>).current = node;
          }}
          {...props}
        >
          {children}
        </SelectItem>
      </TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side="right"
          align="center"
          sideOffset={8}
          collisionPadding={12}
          avoidCollisions
          className="z-[100] max-w-[16rem] rounded-md bg-primary px-3 py-2 text-xs leading-snug text-primary-foreground shadow-lg animate-in fade-in-0 zoom-in-95"
        >
          {description}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
});
ServiceSelectItem.displayName = "ServiceSelectItem";
